import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "@tiptap/markdown";
import Image from "@tiptap/extension-image";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import Placeholder from "@tiptap/extension-placeholder";
import {
  ySyncPluginKey,
  absolutePositionToRelativePosition,
  relativePositionToAbsolutePosition
} from "@tiptap/y-tiptap";
import { Panel, PanelGroup } from "react-resizable-panels";
import * as Y from "yjs";

import { useAccount } from "../../hooks/use-account";
import { getColorForUserName } from "../../utils/getColorForUserName";
import {
  CollaborativeContext,
  useCollaborative,
  useCollaborativeProvider,
  Comment
} from "./use-collaborative";
import { CommentsExtension } from "./comments/CommentsExtension";
import {
  setEditorActiveComment,
  setEditorComments
} from "./comments/CommentHighlightPlugin";
import { CommentsPanel } from "./comments/CommentsPanel";
import { SuggestionMark } from "./suggestions/SuggestionMark";
import {
  SuggestionInterceptorExtension,
  type SuggestionContext
} from "./suggestions/suggestionInterceptor";
import {
  scanSuggestions,
  applySuggestion,
  applyAllSuggestions,
  type Suggestion
} from "./suggestions/suggestionOps";
import { SuggestionsPanel } from "./suggestions/SuggestionsPanel";
import "./suggestions/suggestion.css";

// Scoped style for the "+ Comment" affordance. Topics styles its equivalent
// (CommentSidebar / CommentButton) via styled-components in OutputViewer; the
// shared editor stays self-contained, so it ships its own minimal pill rather
// than depending on a global CSS class or a styled-components import. Injected
// once (id-guarded) so multiple mounted editors don't duplicate the rule.
const COMMENT_ADD_BUTTON_STYLE = `
.comment-add-button {
  background: #ffffff;
  border: 1px solid #ccc;
  border-radius: 9999px;
  padding: 2px 12px;
  font-size: 12px;
  line-height: 1.4;
  color: #2c1561;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 100;
  transition: background 0.2s ease;
}
.comment-add-button:hover {
  background: rgba(44, 21, 97, 0.06);
}
`;

export interface CollaborativeProseEditorProps {
  /** Yjs room name. Empty string disables the provider (engine `isDisabled`). */
  roomName: string;
  /** Mirrors topics' hasWriteAccess gating; passed straight to TipTap `editable`. */
  editable: boolean;
  /** Whether the current user may author comments / replies. */
  canComment: boolean;
  /** Whether to show the resolve/reopen control on comment threads. Default
   *  false so the topics editor (which doesn't pass it) is unchanged. */
  canResolve?: boolean;
  /** Whether the Editing/Suggesting mode toggle is available (tracked changes).
   *  Default false — topics never enables suggesting. */
  canSuggest?: boolean;
  /** Whether the user may accept/reject suggestions (edit-capable). Default false. */
  canAcceptReject?: boolean;
  /** Default true. When false, the editor is read-only and never mounts Collaboration. */
  isLatestVersion?: boolean;
  /** Markdown shown read-only until the Yjs room syncs. */
  fallbackContent?: string;
  placeholder?: string;
  className?: string;
}

export interface CollaborativeProseEditorHandle {
  replaceContent(markdown: string): void;
}

/**
 * Thin TipTap host for BUILDING BLOCKS. Reuses the shared collab engine
 * (`useCollaborativeProvider`), comment plugins (`CommentsExtension`,
 * `setEditorComments`, `setEditorActiveComment`) and the shared `CommentsPanel`.
 *
 * Excludes every topics-only feature (Google-Docs export, mention suggestions,
 * cover image, and the app-only TipTap plugins). It NEVER reads `useTopic`.
 */
export const CollaborativeProseEditor = forwardRef<
  CollaborativeProseEditorHandle,
  CollaborativeProseEditorProps
>(function CollaborativeProseEditor(props, ref) {
  // The engine is the single source of truth; the shared CommentsPanel /
  // CommentsList read it via CollaborativeContext, so we provide it here.
  const engine = useCollaborativeProvider({
    roomName: props.roomName,
    isDisabled: !props.roomName
  });

  return (
    <CollaborativeContext.Provider value={engine}>
      <CollaborativeProseEditorInner {...props} forwardedRef={ref} />
    </CollaborativeContext.Provider>
  );
});

interface InnerProps extends CollaborativeProseEditorProps {
  forwardedRef: React.ForwardedRef<CollaborativeProseEditorHandle>;
}

function CollaborativeProseEditorInner({
  editable,
  canComment,
  canResolve = false,
  canSuggest = false,
  canAcceptReject = false,
  isLatestVersion = true,
  fallbackContent = "",
  placeholder = "Start typing here...",
  className,
  forwardedRef
}: InnerProps) {
  const { vojomoAccount } = useAccount();
  const {
    ydoc,
    provider,
    isSynced,
    isMasterClient,

    localComments,
    activeComment,
    setActiveComment,
    setLocalComments,
    setNewComments,

    createComment,
    updateCommentRef,
    updateOrphanedComments
  } = useCollaborative();

  const containerRef = useRef<HTMLDivElement>(null);

  const [showCommentButton, setShowCommentButton] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ top: 0, right: 8 });
  const [selectedRange, setSelectedRange] = useState({ from: 0, to: 0 });
  const [selectedText, setSelectedText] = useState<string>("");
  const [commentsOpen, setCommentsOpen] = useState(true);

  // Suggesting (tracked-changes) mode. The marks live in the doc (source of
  // truth); localSuggestions is derived by scanning it. getSuggestionContext
  // reads refs so toggling the mode never re-creates the editor.
  const [suggesting, setSuggesting] = useState(false);
  const [localSuggestions, setLocalSuggestions] = useState<Suggestion[]>([]);
  const author = useMemo(
    () => ({
      id: vojomoAccount["_id"] as string,
      name: vojomoAccount.name as string,
      color: getColorForUserName(vojomoAccount.name)
    }),
    [vojomoAccount]
  );
  const suggestingRef = useRef(false);
  const authorRef = useRef(author);
  useEffect(() => {
    suggestingRef.current = canSuggest && suggesting;
  }, [canSuggest, suggesting]);
  useEffect(() => {
    authorRef.current = author;
  }, [author]);
  const getSuggestionContext = useCallback(
    (): SuggestionContext => ({
      suggesting: suggestingRef.current,
      author: authorRef.current
    }),
    []
  );

  const isCollab = isLatestVersion !== false && isSynced;

  // Inject the comment-add-button style once per document (id-guarded).
  useEffect(() => {
    const styleId = "collab-prose-comment-add-button-style";
    if (document.getElementById(styleId)) return;
    const styleEl = document.createElement("style");
    styleEl.id = styleId;
    styleEl.textContent = COMMENT_ADD_BUTTON_STYLE;
    document.head.appendChild(styleEl);
  }, []);

  const onSelectionUpdate = useCallback(
    ({ editor }: { editor: import("@tiptap/react").Editor }) => {
      if (!isSynced) return;

      const { from, to } = editor.state.selection;
      const start = editor.view.coordsAtPos(from);
      const containerBounds = containerRef.current?.getBoundingClientRect();
      const top = containerBounds ? start.top - containerBounds.top : start.top;

      setSelectedRange({ from, to });
      setSelectedText(editor.state.doc.textBetween(from, to, " "));
      setButtonPosition({ top, right: 8 });
      setShowCommentButton(from !== to);
    },
    [isSynced]
  );

  // Base extensions shared by both the read-only and collaborative configs.
  // Topics-only plugins (mentions, ReadOnly*, DisableDragDrop, TableCellSelectAll)
  // are intentionally excluded.
  const editorConfig = useMemo(() => {
    const baseExtensions = [
      StarterKit.configure({ undoRedo: false }),
      Image,
      Markdown,
      CommentsExtension.configure({ ydoc }),
      SuggestionMark
    ];

    if (!isCollab) {
      return {
        extensions: baseExtensions,
        editable: false,
        contentType: "markdown" as const,
        content: fallbackContent,
        onSelectionUpdate
      };
    }

    return {
      extensions: [
        ...baseExtensions,
        Collaboration.configure({
          fragment: ydoc.getXmlFragment("prosemirror")
        }),
        ...(provider?.awareness
          ? [
              CollaborationCaret.configure({
                provider,
                user: {
                  name: vojomoAccount.name,
                  id: vojomoAccount["_id"],
                  color: getColorForUserName(vojomoAccount.name)
                },
                render: (user: { name?: string; color: string }) => {
                  const cursor = document.createElement("span");
                  cursor.classList.add("collab-caret");
                  cursor.style.borderLeft = `2px solid ${user.color}`;
                  cursor.style.marginLeft = "-1px";
                  cursor.style.height = "1em";
                  cursor.style.position = "relative";

                  const label = document.createElement("div");
                  label.className = "collab-label";
                  label.textContent = user.name || "Anonymous";
                  label.style.position = "absolute";
                  label.style.top = "-1.2em";
                  label.style.left = "0";
                  label.style.backgroundColor = user.color;
                  label.style.color = "white";
                  label.style.padding = "0 4px";
                  label.style.fontSize = "0.65em";
                  label.style.borderRadius = "4px";
                  label.style.whiteSpace = "nowrap";

                  cursor.appendChild(label);
                  return cursor;
                }
              })
            ]
          : []),
        Placeholder.configure({
          placeholder,
          showOnlyWhenEditable: true
        }),
        SuggestionInterceptorExtension.configure({
          getContext: getSuggestionContext
        })
      ],
      editable,
      onSelectionUpdate,
      onUpdate: ({ editor }: { editor: import("@tiptap/react").Editor }) =>
        setLocalSuggestions(scanSuggestions(editor.state))
    };
    // `fallbackContent` is intentionally NOT a dependency: it only seeds the
    // pre-sync read-only editor at mount. Including it re-created the live editor
    // whenever the draft prop changed (e.g. synthesis -> onSessionRefresh), which
    // RACED the synthesis `replaceContent` push and dropped the new draft from the
    // edit box. Once synced, the Yjs doc is the source of truth, so the live
    // editor must stay stable across draft-prop changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, provider, isCollab, ydoc, placeholder]);

  const editor = useEditor(editorConfig, [editorConfig]);

  // --- Comment position helpers (lifted verbatim from topics' use-output-editor,
  // they only consume the shared engine's ydoc + the editor's y-sync binding) ---

  const getAbsolutePosition = useCallback(
    (relPosition: Y.RelativePosition) => {
      let absPos = -1;
      try {
        const mapping = ySyncPluginKey.getState(editor!.view.state).binding
          .mapping;

        if (mapping.size > 0) {
          absPos = relativePositionToAbsolutePosition(
            ydoc,
            ydoc.getXmlFragment("prosemirror"),
            relPosition,
            mapping
          );
        }
      } catch (_) {
        /* binding not ready */
      }
      return absPos;
    },
    [editor, ydoc]
  );

  const getRelativePosition = useCallback(
    (absPosition: number) => {
      let relPos: Y.RelativePosition | null = null;
      try {
        const mapping = ySyncPluginKey.getState(editor!.view.state).binding
          .mapping;

        relPos = absolutePositionToRelativePosition(
          absPosition,
          ydoc.getXmlFragment("prosemirror"),
          mapping
        );
      } catch (_) {
        /* binding not ready */
      }
      return relPos;
    },
    [editor, ydoc]
  );

  const handleNewComment = useCallback(() => {
    if (!canComment) return;

    const fromRel = getRelativePosition(selectedRange.from);
    const toRel = getRelativePosition(selectedRange.to);

    createComment({
      parentId: null,
      text: "",
      refText: selectedText,
      fromAbs: selectedRange.from,
      toAbs: selectedRange.to,
      fromRel,
      toRel
    });

    setShowCommentButton(false);
  }, [
    canComment,
    createComment,
    getRelativePosition,
    selectedRange,
    selectedText
  ]);

  // Activate the closest comment when the caret collapses inside a highlight.
  useEffect(() => {
    const yCommentsRef = ydoc.getMap("commentsRef");
    const { from, to } = selectedRange;

    if (from !== to) return;

    let minDistance = Number.MAX_SAFE_INTEGER;
    let closestComment: Comment | null = null;

    localComments
      .filter((c) => !c.parentComment && !c.isOrphaned)
      .forEach((c) => {
        const commRef = yCommentsRef.get(c.id!) as Y.Map<unknown> | undefined;
        if (!commRef) return;

        const cFrom = commRef.get("from") as number;
        const cTo = commRef.get("to") as number;

        if (from >= cFrom && to <= cTo) {
          let currentDistanceFrom = from - cFrom;
          let currentDistanceTo = to - cTo;

          if (currentDistanceFrom < 0)
            currentDistanceFrom = Number.MAX_SAFE_INTEGER;
          if (currentDistanceTo < 0) currentDistanceTo = Number.MAX_SAFE_INTEGER;

          const currentDistance = Math.min(
            currentDistanceFrom,
            currentDistanceTo
          );

          if (currentDistance < minDistance) {
            minDistance = currentDistance;
            closestComment = c;
          }
        }
      });

    setActiveComment(closestComment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localComments, selectedRange]);

  // Keep the ProseMirror highlight decorations in sync with engine state.
  useEffect(() => {
    if (editor) setEditorComments(editor, localComments);
  }, [editor, localComments]);

  useEffect(() => {
    if (editor) setEditorActiveComment(editor, activeComment);
  }, [editor, activeComment]);

  // Observe the Yjs comments map and project it into the engine's localComments.
  useEffect(() => {
    if (!isSynced) return;

    const yMap = ydoc.getMap("comments");

    const handleChange = () => {
      let areAllInitialized = true;

      for (const commentId of yMap.keys()) {
        const comm = yMap.get(commentId) as Y.Map<unknown>;
        if (comm.get("isInitialized") === false) {
          areAllInitialized = false;
          break;
        }
      }

      if (!areAllInitialized) return;

      const commentsLocal: Comment[] = [];

      for (const commentId of yMap.keys()) {
        const comm = yMap.get(commentId) as Y.Map<unknown>;
        if (comm.get("isDeleted")) continue;

        const fromRelRaw = comm.get("fromRel");
        const toRelRaw = comm.get("toRel");
        const subCommArr = comm.get("subComments");
        const newComm: Comment = {
          id: comm.get("id") as string | undefined,
          updatedAt: comm.get("updatedAt") as string | Date | undefined,
          owner: comm.get("owner") as Partial<import("../../types/dbTypes").User> | undefined,
          fromRel: fromRelRaw != null
            ? Y.decodeRelativePosition(fromRelRaw as Uint8Array)
            : undefined,
          toRel: toRelRaw != null
            ? Y.decodeRelativePosition(toRelRaw as Uint8Array)
            : undefined,
          refText: comm.get("refText") as string | undefined,
          text: (comm.get("text") as string) ?? "",
          subComments: subCommArr != null
            ? (subCommArr as Y.Array<unknown>).toArray().map(String)
            : undefined,
          parentComment: comm.get("parentComment") as string | undefined,
          isResolved: comm.get("isResolved") as boolean | undefined,
          isDeleted: comm.get("isDeleted") as boolean | undefined,
          isOrphaned: comm.get("isOrphaned") as boolean | undefined,
        };
        commentsLocal.push(newComm);
      }

      const yCommentsRef = ydoc.getMap("commentsRef");

      commentsLocal.sort((a, b) => {
        const aRef = yCommentsRef.get(a.id!) as Y.Map<unknown> | undefined;
        const bRef = yCommentsRef.get(b.id!) as Y.Map<unknown> | undefined;

        const aFrom = (aRef?.get("from") as number | undefined) ?? null;
        const bFrom = (bRef?.get("from") as number | undefined) ?? null;

        if (aFrom === null) return 1;
        if (bFrom === null) return -1;

        return aFrom - bFrom;
      });

      setLocalComments((prev) => {
        const arr = commentsLocal.filter((c) => {
          if (c.owner?.id !== vojomoAccount["_id"] && !c.text) return false;
          return true;
        });

        if (prev.length > 0) {
          const filteredComments = commentsLocal.filter(
            (c) => c.owner?.id !== vojomoAccount["_id"] && !!c.text
          );

          const newestComments: string[] = [];

          for (const c of filteredComments) {
            const prevComm = prev.find((pc) => pc.id === c.id);
            if (!prevComm || prevComm.text !== c.text) {
              newestComments.push(c.id!);
            }
          }

          setNewComments(newestComments);
        }

        return arr;
      });
    };

    handleChange();
    yMap.observeDeep(handleChange);

    return () => yMap.unobserveDeep(handleChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ydoc, isSynced]);

  // Master client initializes relative positions for freshly created comments.
  useEffect(() => {
    if (!isSynced || !isMasterClient) return;

    const yMap = ydoc.getMap("comments");
    const yCommentsRef = ydoc.getMap("commentsRef");

    const commsToInitialize: string[] = [];

    for (const commentId of yMap.keys()) {
      const comm = yMap.get(commentId) as Y.Map<unknown>;
      if (!comm.get("parentComment") && comm.get("isInitialized") === false) {
        commsToInitialize.push(commentId);
      }
    }

    if (commsToInitialize.length > 0) {
      ydoc.transact(() => {});
      for (const commentId of commsToInitialize) {
        const commRef = yCommentsRef.get(commentId) as Y.Map<unknown>;
        const comm = yMap.get(commentId) as Y.Map<unknown>;

        const fromRel = getRelativePosition(commRef.get("from") as number);
        const toRel = getRelativePosition(commRef.get("to") as number);

        if (fromRel && toRel) {
          comm.set("fromRel", Y.encodeRelativePosition(fromRel));
          comm.set("toRel", Y.encodeRelativePosition(toRel));
        }

        comm.set("isInitialized", true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSynced, isMasterClient]);

  // Master client re-anchors comments and flags orphans as the document changes.
  useEffect(() => {
    if (!isSynced || !isMasterClient || !editor) return;

    const commentsOrphanedStatus: { id: string; payload: object }[] = [];

    for (const comment of localComments.filter((c) => !c.parentComment)) {
      let fromAbs: number | null = null;
      let toAbs: number | null = null;

      try {
        fromAbs = getAbsolutePosition(comment.fromRel);
        toAbs = getAbsolutePosition(comment.toRel);
      } catch (err) {
        console.error("Error at getting abs positions", err);
      }

      if (fromAbs === -1 || toAbs === -1) continue;

      const isOrphaned =
        fromAbs === null || toAbs === null || fromAbs === toAbs;

      if (!isOrphaned && fromAbs !== null && toAbs !== null) {
        const refText = editor.state.doc.textBetween(fromAbs, toAbs, " ");
        updateCommentRef(comment.id!, refText, fromAbs, toAbs);
      }

      if (isOrphaned && !comment.isOrphaned) {
        commentsOrphanedStatus.push({
          id: comment.id!,
          payload: { isOrphaned: true }
        });
      } else if (!isOrphaned && comment.isOrphaned && fromAbs !== null && toAbs !== null) {
        const fromRel = getRelativePosition(fromAbs);
        const toRel = getRelativePosition(toAbs);
        commentsOrphanedStatus.push({
          id: comment.id!,
          payload: {
            isOrphaned: false,
            ...(fromRel ? { fromRel: Y.encodeRelativePosition(fromRel) } : {}),
            ...(toRel ? { toRel: Y.encodeRelativePosition(toRel) } : {})
          }
        });
      }
    }

    if (commentsOrphanedStatus.length > 0) {
      updateOrphanedComments(commentsOrphanedStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ydoc, isSynced, localComments, isMasterClient, editor]);

  // Imperative replaceContent: replace editor content via a single editor-command
  // chain so the Collaboration binding is the sole writer to the Yjs fragment.
  // Used by the synthesis-load task (D2). Changes propagate live via the
  // Collaboration binding and autosave via the engine's debounce.
  useImperativeHandle(
    forwardedRef,
    () => ({
      replaceContent(markdown: string) {
        if (!editor) return;
        editor
          .chain()
          .clearContent()
          .insertContent(markdown, { contentType: "markdown" })
          .run();
      }
    }),
    [editor]
  );

  // In Suggesting mode the editor must be typable even for users who can't
  // directly edit (comment-capable): their keystrokes become suggestions via the
  // interceptor. Done imperatively so toggling mode never re-creates the editor.
  useEffect(() => {
    if (editor && isCollab) {
      editor.setEditable(editable || (canSuggest && suggesting));
    }
  }, [editor, isCollab, editable, canSuggest, suggesting]);

  // Populate the suggestion list once the editor + room are ready (covers
  // suggestions already present at load; live edits update via onUpdate).
  useEffect(() => {
    if (editor && isSynced) setLocalSuggestions(scanSuggestions(editor.state));
  }, [editor, isSynced]);

  const handleAcceptSuggestion = useCallback(
    (id: string) => {
      if (!editor) return;
      applySuggestion(editor, id, "accept");
      setLocalSuggestions(scanSuggestions(editor.state));
    },
    [editor]
  );
  const handleRejectSuggestion = useCallback(
    (id: string) => {
      if (!editor) return;
      applySuggestion(editor, id, "reject");
      setLocalSuggestions(scanSuggestions(editor.state));
    },
    [editor]
  );
  const handleAcceptAll = useCallback(() => {
    if (!editor) return;
    applyAllSuggestions(editor, "accept");
    setLocalSuggestions(scanSuggestions(editor.state));
  }, [editor]);
  const handleRejectAll = useCallback(() => {
    if (!editor) return;
    applyAllSuggestions(editor, "reject");
    setLocalSuggestions(scanSuggestions(editor.state));
  }, [editor]);

  // Self-contained layout: the editor lives in one `<Panel>`, the shared
  // `<CommentsPanel>` (which itself renders a `<PanelResizeHandle />` + `<Panel>`)
  // follows, and both sit inside a horizontal `<PanelGroup>`. This mirrors
  // TopicViewer's layout so the comments panel has the `<PanelGroup>` ancestor
  // it needs — making this component mountable standalone (e.g. RB block tabs).
  //
  // Height is CONTENT-DRIVEN: the PanelGroup is not forced to `h-full`, and the
  // editor container self-sizes (`min-h` floor, grows with text, `max-h` cap
  // then internal scroll). `react-resizable-panels` only manages the horizontal
  // split (editor ↔ comments) via flex-basis; the cross-axis height stretches to
  // the tallest child, which is the content-sized editor container.
  return (
    <div className={className}>
      {canSuggest && (
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] font-medium text-muted-foreground">Mode</span>
          <div className="inline-flex items-center rounded-full p-0.5 bg-slate-100 border border-slate-200">
            {([
              { key: false, label: "Editing" },
              { key: true, label: "Suggesting" }
            ] as const).map(({ key, label }) => (
              <button
                key={label}
                type="button"
                onClick={() => setSuggesting(key)}
                className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold transition-all border-0 cursor-pointer ${
                  suggesting === key
                    ? "bg-white text-[#2c1561] shadow-sm"
                    : "bg-transparent text-slate-500 hover:text-slate-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}
      {canSuggest && (
        <SuggestionsPanel
          suggestions={localSuggestions}
          canAcceptReject={canAcceptReject}
          onAccept={handleAcceptSuggestion}
          onReject={handleRejectSuggestion}
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
        />
      )}
      <PanelGroup direction="horizontal" className="min-h-[96px] max-h-[55vh]">
      <Panel defaultSize={80} minSize={50} className="min-w-0">
        <div
          ref={containerRef}
          className="relative min-h-[96px] max-h-[55vh] overflow-y-auto"
        >
          <EditorContent editor={editor} />

          {canComment && showCommentButton && (
            <button
              type="button"
              className="comment-add-button"
              style={{
                position: "absolute",
                top: buttonPosition.top,
                right: buttonPosition.right
              }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleNewComment}
            >
              + Comment
            </button>
          )}
        </div>
      </Panel>

      <CommentsPanel
        commentsOpen={commentsOpen}
        setCommentsOpen={setCommentsOpen}
        comments={localComments}
        canComment={canComment}
        canResolve={canResolve}
      />
      </PanelGroup>
    </div>
  );
}
